import { toastState } from "@/atoms/system/toast-state";
import { GLOBAL_ERROR } from "@/common/constant/global-error";
import { usePrompt } from "@/common/custom-hook/use-promt";
import { AdminItemPath } from "@/common/enum/path";
import { ItemCreateHeader } from "@/components/header/ItemCreateHeader";
import { CreateItemModal } from "@/components/modal/CreateItemModal";
import { ItemCreatePanel } from "@/components/panel/ItemCreatePanel";
import { PageWrapper } from "@/components/wrapper/PageWrapper";
import { itemService } from "@/services/item-service";
import { CreateItemPayload } from "@/types/payload/create-item-payload";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSetRecoilState } from "recoil";
import validator from "validator";
import { ItemCodeModal } from "../../../components/modal/ItemCodeModal";
import { getTextLength } from "@/common/util/jsonUtils";

const initCreateItemPayload: CreateItemPayload = {
  name: "",
  files: [],
  categoryId: 1,
  details: "",
  features: "",
  modelName: "",
  erpCode: "",
  isCataloged: false,
  memos: [],
  link: "",
};

export default function SingleProductCreatePage() {
  const [createItemPayload, setCreateItemPayload] = useState<CreateItemPayload>(
    initCreateItemPayload
  );
  const [isOpenCreateItemModal, setIsOpenCreateItemModal] = useState(false);
  const [isUrlValid, setIsUrlValid] = useState(true);
  const setToast = useSetRecoilState(toastState);
  const isActive =
    !validator.isEmpty(createItemPayload.name) &&
    validator.isInt(String(createItemPayload.categoryId), { min: 2 }) &&
    !validator.isEmpty(createItemPayload.details) &&
    !validator.isEmpty(createItemPayload.features) &&
    !validator.isEmpty(createItemPayload.erpCode) &&
    !validator.isEmpty(createItemPayload.modelName) &&
    // 메모가 있다면, 최소 하나는 공백 제외 1글자 이상이어야 함
    (createItemPayload.memos.length === 0 ||
      createItemPayload.memos.some((memo) => memo.trim().length >= 1))
    && isUrlValid
    ;

  const [blocked, setBlocked] = useState(true);

  const navigate = useNavigate();

  const [isOpenItemCodeModal, setIsOpenItemCodeModal] = useState(false);

  const urlRegex = /^https?:\/\/([\w-]+(\.[\w-]+)+)(:\d+)?(\/\S*)?$/;


  const handleMemoChange = (index: number, value: string) => {
    setCreateItemPayload((prevPayload) => {
      const newMemos = [...prevPayload.memos];
      newMemos[index] = value;
      return {
        ...prevPayload,
        memos: newMemos,
      };
    });
  };

  const handleAddMemo = () => {
    setCreateItemPayload((prevPayload) => {
      // 메모가 10개 이상이면 추가하지 않음
      if (prevPayload.memos.length >= 10) return prevPayload;

      return {
        ...prevPayload,
        memos: [...prevPayload.memos, ""],
      };
    });
  };

  const handleRemoveMemo = (index: number) => {
    setCreateItemPayload((prevPayload) => ({
      ...prevPayload,
      memos: prevPayload.memos.filter((_, i) => i !== index),
    }));
  };

  const handleOpenItemCodeModal = () => {
    setIsOpenItemCodeModal(true);
  };

  const handleCloseItemCodeModal = () => {
    setIsOpenItemCodeModal(false);
  };

  const handleOpenCreateItemModal = () => {
    setIsOpenCreateItemModal(true);
  };

  const handleCloseCreateItemModal = () => {
    setIsOpenCreateItemModal(false);
  };

  const handleFileChange = (files: File[]) => {
    setCreateItemPayload((prevPayload) => ({
      ...prevPayload,
      files: files,
    }));
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    if (name === "link") {
      setIsUrlValid(urlRegex.test(value));
    }

    setCreateItemPayload((prevPayload) => ({
      ...prevPayload,
      [name]: value,
    }));
  };

  const handleCatalogChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setCreateItemPayload((prevPayload) => ({
      ...prevPayload,
      isCataloged: value === "YES",
    }));
  };

  const handleCreateButtonClick = async () => {
    if (getTextLength(createItemPayload.details) > 1000) {
      alert("상품 설명은 최대 1000자 이하이어야 합니다.");
      return;
    }

    if (getTextLength(createItemPayload.features) > 1000) {
      alert("기능 설명은 최대 1000자 이하이어야 합니다.");
      return;
    }

    // URL 유효성 검사
    if (createItemPayload.link && !urlRegex.test(createItemPayload.link)) {
      alert("유효한 URL을 입력해주세요.");
      return;
    }

    try {
      const createResponse = await itemService.create(createItemPayload);
      if (!createResponse.ok) {
        const errorArray = Object.values(
          createResponse.data.aggregateErrors || {}
        );
        if (errorArray.length > 0) {
          alert(`${errorArray[0]}`);
        } else {
          alert(GLOBAL_ERROR);
        }
        return;
      }
      const getByIdResponse = await itemService.getById({
        id: createResponse.data.id,
      });
      setCreateItemPayload({
        ...createItemPayload,
        name: getByIdResponse.data.name,
        categoryId: getByIdResponse.data.category.id,
        details: getByIdResponse.data.details,
        features: getByIdResponse.data.features,
        modelName: getByIdResponse.data.modelName,
        erpCode: getByIdResponse.data.erpCode || "",
        isCataloged: getByIdResponse.data.isCataloged,
        memos: getByIdResponse.data.memos || [],
      });
      setToast({
        show: true,
        messages: [`상품이 등록되었습니다.`],
      });
      handleCloseCreateItemModal();
      setBlocked(false);
      navigate(AdminItemPath.SINGLE_PRODUCT);
    } catch (error) {
      console.error("Error creating item:", error);
    }
  };

  const updateCategoryId = (categoryId: number) => {
    setCreateItemPayload((prevPayload) => ({
      ...prevPayload,
      categoryId,
    }));
  };

  const setErpCode = (erpCode: string) => {
    setCreateItemPayload((prevPayload) => ({
      ...prevPayload,
      erpCode,
    }));
  };

  usePrompt(
    "사이트에서 나가시겠습니까? 변경사항이 저장되지 않을 수 있습니다.",
    blocked
  );

  return (
    <PageWrapper>
      <div className="flex flex-col w-[1648px]">
        <ItemCreateHeader
          isActive={isActive}
          handleOpenCreateItemModal={handleOpenCreateItemModal}
        />
        <ItemCreatePanel
          createItemPayload={createItemPayload}
          updateCategoryId={updateCategoryId}
          handleFileChange={handleFileChange}
          handleChange={handleChange}
          handleCatalogChange={handleCatalogChange}
          handleOpenItemCodeModal={handleOpenItemCodeModal}
          handleMemoChange={handleMemoChange}
          handleAddMemo={handleAddMemo}
          handleRemoveMemo={handleRemoveMemo}
          isUrlValid={isUrlValid}
        />
      </div>
      {isOpenCreateItemModal && (
        <CreateItemModal
          handleSubmit={handleCreateButtonClick}
          closeModal={handleCloseCreateItemModal}
        />
      )}
      {isOpenItemCodeModal && (
        <ItemCodeModal
          erpCode={createItemPayload.erpCode}
          setErpCode={setErpCode}
          closeModal={handleCloseItemCodeModal}
        />
      )}
    </PageWrapper>
  );
}
